import { CalendarToday, FiberManualRecord, LocationOn, OpenInNew, Schedule, Stadium, Tag } from '@mui/icons-material';
import { Box, Button, CircularProgress, Paper, Tooltip, Typography } from '@mui/material';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';

import { capitalizar, formatFechaPartido, formatLigaName } from '../../utils/UtilesComun';
import { BoxButtonApp } from '../custom/CustomComponents';

/**
 * Datos del partido en formato de contenedor vertical modernizado y limpio.
 */
const DatosPartido = ({ datosPartido, loading = false }) => {
  if (loading && (!datosPartido || !datosPartido.idPartido)) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <BoxButtonApp sx={{ textAlign: 'center', py: 0.75, opacity: 0.6, pointerEvents: 'none' }}>
          <CircularProgress size={16} sx={{ color: 'white' }} />
        </BoxButtonApp>
        <Paper
          elevation={0}
          sx={{
            borderRadius: '12px',
            border: '1px solid #e2e8f0',
            backgroundColor: '#ffffff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: 280,
          }}
        >
          <CircularProgress size={32} />
        </Paper>
      </Box>
    );
  }

  if (!datosPartido || !datosPartido.idPartido) {
    return null;
  }

  const jugado =
    datosPartido.jugado !== undefined
      ? Boolean(datosPartido.jugado)
      : datosPartido.golesLocal !== null && datosPartido.golesLocal !== undefined;

  const infoFecha = formatFechaPartido(datosPartido.fecha);
  const fechaPartes = (datosPartido.fecha || '').split(',');
  const fechaTextoRaw = fechaPartes[0]?.trim();
  const horaTextoRaw = fechaPartes[1]?.trim();

  // Fecha enriquecida con el día de la semana (ej. "Sábado, 10/10/2026")
  let fechaTexto = '';
  if (infoFecha.diaSemCompleto) {
    const yaTieneDia =
      fechaTextoRaw &&
      (fechaTextoRaw.toLowerCase().includes(infoFecha.diaSem.toLowerCase()) ||
        fechaTextoRaw.toLowerCase().includes(infoFecha.diaSemCompleto.toLowerCase()));
    fechaTexto = yaTieneDia ? fechaTextoRaw : `${infoFecha.diaSemCompleto}, ${infoFecha.fechaAnio || fechaTextoRaw}`;
  } else {
    fechaTexto = fechaTextoRaw || (infoFecha.fecha !== '-' ? infoFecha.fecha : '');
  }

  const horaTexto = infoFecha.hora || horaTextoRaw;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {/* Botón de acceso a la Liga */}
      <Link to={`/liga/${datosPartido.idLiga}`} style={{ textDecoration: 'none' }}>
        <BoxButtonApp sx={{ textAlign: 'center', py: 0.75 }}>
          {formatLigaName(datosPartido.categoria, datosPartido.division, datosPartido.grupo)}
        </BoxButtonApp>
      </Link>

      {/* Tarjeta limpia y moderna del Partido */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: '12px',
          border: '1px solid #e2e8f0',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.04), 0 2px 4px -2px rgba(0, 0, 0, 0.03)',
          backgroundColor: '#ffffff',
          overflow: 'hidden',
        }}
      >
        {/* Cabecera / Marcador */}
        <Box
          sx={{
            py: 2.5,
            px: 2,
            textAlign: 'center',
            borderBottom: '1px solid #e2e8f0',
            backgroundColor: '#ffffff',
          }}
        >
          {/* Badge de estado del partido */}
          <Box sx={{ display: 'inline-flex', alignItems: 'center', mb: 1.25 }}>
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.6,
                px: 1.25,
                py: 0.35,
                borderRadius: '12px',
                backgroundColor: jugado ? '#ecfdf5' : '#f8fafc',
                color: jugado ? '#047857' : '#64748b',
                border: `1px solid ${jugado ? '#a7f3d0' : '#e2e8f0'}`,
                fontSize: '0.68rem',
                fontWeight: 700,
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
              }}
            >
              <FiberManualRecord
                sx={{
                  fontSize: '0.55rem',
                  color: jugado ? '#10b981' : '#94a3b8',
                }}
              />
              {jugado ? 'Finalizado' : 'Por disputar'}
            </Box>
          </Box>

          {/* Marcador */}
          {jugado ? (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 2,
              }}
            >
              <Typography
                sx={{
                  fontSize: '2.8rem',
                  fontWeight: 800,
                  color: '#0f172a',
                  lineHeight: 1,
                  minWidth: 42,
                  textAlign: 'right',
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {datosPartido.golesLocal ?? 0}
              </Typography>
              <Typography
                sx={{
                  fontSize: '2rem',
                  fontWeight: 300,
                  color: '#cbd5e1',
                  lineHeight: 1,
                  userSelect: 'none',
                }}
              >
                –
              </Typography>
              <Typography
                sx={{
                  fontSize: '2.8rem',
                  fontWeight: 800,
                  color: '#0f172a',
                  lineHeight: 1,
                  minWidth: 42,
                  textAlign: 'left',
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {datosPartido.golesVisitante ?? 0}
              </Typography>
            </Box>
          ) : (
            <Typography
              sx={{
                fontSize: '2rem',
                fontWeight: 700,
                color: '#64748b',
                letterSpacing: 3,
                my: 0.5,
              }}
            >
              VS
            </Typography>
          )}
        </Box>

        {/* Lista de Detalles - Diseño aireado y limpio */}
        <Box sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
          {/* Jornada */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              py: 1.25,
              borderBottom: '1px solid #f1f5f9',
            }}
          >
            <Box
              sx={{
                width: 28,
                height: 28,
                minWidth: 28,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'primary.main',
              }}
            >
              <Tag sx={{ fontSize: '1.15rem' }} />
            </Box>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography
                sx={{
                  fontSize: '0.68rem',
                  fontWeight: 600,
                  color: '#94a3b8',
                  textTransform: 'uppercase',
                  letterSpacing: '0.04em',
                  lineHeight: 1.1,
                }}
              >
                Competición
              </Typography>
              <Typography
                sx={{
                  fontSize: '0.85rem',
                  fontWeight: 600,
                  color: '#1e293b',
                  lineHeight: 1.35,
                }}
              >
                Jornada {datosPartido.jornada}
              </Typography>
            </Box>
          </Box>

          {/* Fecha */}
          {fechaTexto && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                py: 1.25,
                borderBottom: '1px solid #f1f5f9',
              }}
            >
              <Box
                sx={{
                  width: 28,
                  height: 28,
                  minWidth: 28,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'primary.main',
                }}
              >
                <CalendarToday sx={{ fontSize: '1.05rem' }} />
              </Box>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography
                  sx={{
                    fontSize: '0.68rem',
                    fontWeight: 600,
                    color: '#94a3b8',
                    textTransform: 'uppercase',
                    letterSpacing: '0.04em',
                    lineHeight: 1.1,
                  }}
                >
                  Fecha
                </Typography>
                <Typography
                  sx={{
                    fontSize: '0.85rem',
                    fontWeight: 600,
                    color: '#1e293b',
                    lineHeight: 1.35,
                  }}
                >
                  {fechaTexto}
                </Typography>
              </Box>
            </Box>
          )}

          {/* Hora - Con la misma importancia que la fecha */}
          {horaTexto && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                py: 1.25,
                borderBottom: '1px solid #f1f5f9',
              }}
            >
              <Box
                sx={{
                  width: 28,
                  height: 28,
                  minWidth: 28,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'primary.main',
                }}
              >
                <Schedule sx={{ fontSize: '1.1rem' }} />
              </Box>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography
                  sx={{
                    fontSize: '0.68rem',
                    fontWeight: 600,
                    color: '#94a3b8',
                    textTransform: 'uppercase',
                    letterSpacing: '0.04em',
                    lineHeight: 1.1,
                  }}
                >
                  Hora
                </Typography>
                <Typography
                  sx={{
                    fontSize: '0.85rem',
                    fontWeight: 600,
                    color: '#1e293b',
                    lineHeight: 1.35,
                  }}
                >
                  {horaTexto}
                </Typography>
              </Box>
            </Box>
          )}

          {/* Campo */}
          {datosPartido.campo && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                py: 1.25,
                borderBottom: datosPartido.localidad ? '1px solid #f1f5f9' : 'none',
              }}
            >
              <Box
                sx={{
                  width: 28,
                  height: 28,
                  minWidth: 28,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'primary.main',
                }}
              >
                <Stadium sx={{ fontSize: '1.15rem' }} />
              </Box>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography
                  sx={{
                    fontSize: '0.68rem',
                    fontWeight: 600,
                    color: '#94a3b8',
                    textTransform: 'uppercase',
                    letterSpacing: '0.04em',
                    lineHeight: 1.1,
                  }}
                >
                  Estadio
                </Typography>
                <Typography
                  sx={{
                    fontSize: '0.85rem',
                    fontWeight: 600,
                    color: '#1e293b',
                    lineHeight: 1.35,
                    wordBreak: 'break-word',
                  }}
                >
                  {capitalizar(datosPartido.campo)}
                </Typography>
              </Box>
            </Box>
          )}

          {/* Localidad */}
          {datosPartido.localidad && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                py: 1.25,
              }}
            >
              <Box
                sx={{
                  width: 28,
                  height: 28,
                  minWidth: 28,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'primary.main',
                }}
              >
                <LocationOn sx={{ fontSize: '1.15rem' }} />
              </Box>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography
                  sx={{
                    fontSize: '0.68rem',
                    fontWeight: 600,
                    color: '#94a3b8',
                    textTransform: 'uppercase',
                    letterSpacing: '0.04em',
                    lineHeight: 1.1,
                  }}
                >
                  Ubicación
                </Typography>
                <Typography
                  sx={{
                    fontSize: '0.85rem',
                    fontWeight: 600,
                    color: '#1e293b',
                    lineHeight: 1.35,
                  }}
                >
                  {datosPartido.localidad}
                  {datosPartido.provincia && ` (${datosPartido.provincia})`}
                </Typography>
              </Box>
            </Box>
          )}

          {/* Enlace al Acta o Vídeo oficial si existe */}
          {datosPartido.enlace && (
            <Tooltip title="Abrir acta o vídeo oficial del partido" arrow>
              <Button
                variant="outlined"
                size="small"
                component="a"
                href={datosPartido.enlace}
                target="_blank"
                rel="noopener noreferrer"
                endIcon={<OpenInNew sx={{ fontSize: '0.85rem !important' }} />}
                sx={{
                  mt: 1.5,
                  fontSize: '0.75rem',
                  textTransform: 'none',
                  fontWeight: 600,
                  borderRadius: '8px',
                  borderColor: '#cbd5e1',
                  color: '#334155',
                  py: 0.75,
                  '&:hover': {
                    borderColor: 'primary.main',
                    backgroundColor: 'rgba(15, 118, 110, 0.04)',
                    color: 'primary.main',
                  },
                }}
              >
                Acta oficial del partido
              </Button>
            </Tooltip>
          )}
        </Box>
      </Paper>
    </Box>
  );
};

DatosPartido.propTypes = {
  datosPartido: PropTypes.shape({
    idPartido: PropTypes.number,
    idLiga: PropTypes.number,
    golesLocal: PropTypes.number,
    golesVisitante: PropTypes.number,
    jugado: PropTypes.bool,
    enlace: PropTypes.string,
    jornada: PropTypes.number,
    fecha: PropTypes.string,
    campo: PropTypes.string,
    localidad: PropTypes.string,
    provincia: PropTypes.string,
    categoria: PropTypes.string,
    division: PropTypes.string,
    grupo: PropTypes.string,
  }),
  loading: PropTypes.bool,
};

DatosPartido.defaultProps = {
  datosPartido: {
    idPartido: 0,
    idLiga: 0,
    golesLocal: 0,
    golesVisitante: 0,
    jugado: false,
    enlace: null,
    jornada: 0,
    fecha: '',
    campo: '',
    localidad: '',
    provincia: '',
    categoria: '',
    division: '',
    grupo: '',
  },
};

export default DatosPartido;
